import { InputAdornment, styled, Typography } from '@chronosphereio/chrono-ui';
import { TimeRangeSelectLastLoaded, TimeRangeSelectLastLoadedProps } from './TimeRangeSelectLastLoaded';
import { TimeRangeSelectTimeZone, TimeRangeSelectTimeZoneProps } from './TimeRangeSelectTimeZone';

export type TimeRangeSelectEndAdornmentProps = TimeRangeSelectLastLoadedProps & TimeRangeSelectTimeZoneProps;

/**
 * Custom end adornments for `TimeRangeSelect` that are rendered before the dropdown arrow automatically
 * included by the `AutoComplete`.
 */
export const TimeRangeSelectEndAdornment = ({
  lastLoadedMessage,
  onLastLoadedMessageClick,
  onTimeZoneClick,
  timeZoneDetails,
  userTimeZone,
  disableTimeZoneOverride,
}: TimeRangeSelectEndAdornmentProps) => {
  const shouldShowTimeRangeLastLoaded = !!lastLoadedMessage;

  return (
    <InputAdornment
      position="end"
      sx={{
        // Normalizes the spacing to more closely match mocks.
        marginLeft: (theme) => (shouldShowTimeRangeLastLoaded ? 0 : theme.spacing(0.5)),
      }}
    >
      {/* Wrapping div needed for text alignment in typography below to work properly because of flex parent. */}
      <div>
        <VerticalAlignSmallerText variant="body2" component="div">
          <TimeRangeSelectTimeZone
            userTimeZone={userTimeZone}
            timeZoneDetails={timeZoneDetails}
            onTimeZoneClick={onTimeZoneClick}
            disableTimeZoneOverride={disableTimeZoneOverride}
          />
        </VerticalAlignSmallerText>
      </div>
      {shouldShowTimeRangeLastLoaded && (
        <Separator variant="body2" role="separator">
          |
        </Separator>
      )}
      {/* Wrapping div needed for text alignment in typography below to work properly because of flex parent. */}
      {shouldShowTimeRangeLastLoaded && (
        <div>
          <VerticalAlignSmallerText variant="body2" component="div">
            <TimeRangeSelectLastLoaded
              lastLoadedMessage={lastLoadedMessage}
              onLastLoadedMessageClick={onLastLoadedMessageClick}
            />
          </VerticalAlignSmallerText>
        </div>
      )}
    </InputAdornment>
  );
};

const Separator = styled(Typography)(({ theme }) => {
  return {
    marginRight: theme.spacing(0.5),
    color: theme.palette.text.secondary,
  };
});

const VerticalAlignSmallerText = styled(Typography)(({ theme }) => {
  return {
    // Align the smaller text here with the larger main text in the select dropdown.
    // Hardcoded instead of trying to pull from the theme because there isn't a straightforward theme
    // value to pull from. Looking at the MUI code, it's a combination of the theme plus some rem and em values
    // that would be tricky to calculate here. Visual tests will help us catch regressions.
    lineHeight: '20.125px',
    verticalAlign: 'middle',
    display: 'inline-flex',
    marginRight: theme.spacing(0.5),
    color: theme.palette.text.secondary,
    gap: theme.spacing(0.5),
  };

  // Type casting needed because of the `component` prop, styled, and typescript
  // not playing nicely together.
  // https://mui.com/material-ui/guides/typescript/#complications-with-the-component-prop
}) as unknown as typeof Typography;
